import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, Lock, LogOut, Menu, Wifi, WifiOff } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ROLES } from "@/lib/permissions";
import { get } from "@/lib/api";
import { Button } from "@/components/ui/button";

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return <span data-testid="topbar-clock" className="font-mono text-xs text-muted-foreground">{now.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" })} · {now.toLocaleTimeString("fr-FR")}</span>;
};

const EntrepriseSelector = () => {
  const { entrepriseId, setEntrepriseId } = useAuth();
  const { data } = useQuery({ queryKey: ["entreprises"], queryFn: () => get("/entreprises") });
  useEffect(() => { if (!entrepriseId && data?.length) setEntrepriseId(data[0].id); }, [data, entrepriseId, setEntrepriseId]);
  return (
    <select data-testid="entreprise-selector" value={entrepriseId || ""} onChange={(e) => setEntrepriseId(Number(e.target.value))}
      className="h-7 bg-input border border-border rounded-sm text-xs px-2 font-medium max-w-[220px]">
      {(data || []).map((e) => <option key={e.id} value={e.id}>{e.nom}{e.actif ? "" : " (désactivée)"}</option>)}
    </select>
  );
};

export const Topbar = ({ onMenu }) => {
  const { user, isDemo, logout } = useAuth();
  const { isError } = useQuery({ queryKey: ["health"], queryFn: () => get("/health"), refetchInterval: 30000, retry: false });
  return (
    <header data-testid="topbar" className="h-12 flex items-center justify-between gap-3 px-4 border-b border-border bg-slate-900/95 backdrop-blur-md sticky top-0 z-40">
      <div className="flex items-center gap-3 min-w-0">
        <button onClick={onMenu} data-testid="mobile-menu-button" className="lg:hidden h-7 w-7 flex items-center justify-center border border-border rounded-sm text-muted-foreground hover:text-foreground shrink-0"><Menu className="h-4 w-4" /></button>
        <div className="flex items-center gap-1.5 text-xs font-medium min-w-0">
          <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          {user.role === "superadmin" ? <EntrepriseSelector /> : <span data-testid="topbar-entreprise" className="truncate">{user.entreprise || "—"}</span>}
        </div>
        {isDemo && <span data-testid="demo-badge" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-300 rounded-sm whitespace-nowrap"><Lock className="h-3 w-3" /><span className="hidden sm:inline">Démo · lecture seule</span><span className="sm:hidden">Démo</span></span>}
      </div>
      <div className="flex items-center gap-4">
        <span data-testid="api-status" className={`hidden sm:inline-flex items-center gap-1 text-[10px] font-mono uppercase ${isError ? "text-red-400" : "text-emerald-400"}`}>
          {isError ? <WifiOff className="h-3 w-3" /> : <Wifi className="h-3 w-3" />}{isError ? "API hors ligne" : "API connectée"}
        </span>
        <span className="hidden md:inline"><Clock /></span>
        <div className="flex items-center gap-2 pl-3 border-l border-border">
          <div className="text-right leading-tight hidden sm:block">
            <div data-testid="topbar-user-name" className="text-xs font-semibold">{user.nom}</div>
            <div data-testid="topbar-user-role" className="eyebrow">{ROLES[user.role]?.label || user.role}</div>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={logout} title="Déconnexion" data-testid="logout-button"><LogOut className="h-4 w-4" /></Button>
        </div>
      </div>
    </header>
  );
};
